import React from 'react';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectCurrentSpace } from '@/store/selectors/spaceSelectors';
// 团队管理项暂去掉，恢复时解除注释（visibility.showTeamManage 显隐）
// 充值按钮注释隐藏后 useCurrentIdentity 暂无消费者，恢复时一并解注释
// import { useCurrentIdentity } from '@/hooks/useCurrentIdentity';
import { useUserPackage } from '@/features/ricegrain/hooks/useUserPackage';
import { useBrand } from '@/contexts/BrandContext';
import { clearAllUserData } from '@/utils/auth';
import authService from '@/services/amai/auth.service';
import { eventBus } from '@/utils/eventBus';
import { spaceManager } from '@/services/space/spaceManager';
import { AccountListItem } from '@/services/types';
import { TeamRole } from '@/store/slices/spaceSlice';
import SwitchAccountPanel from './SwitchAccountPanel';
import WeChatQRCode from '@/assets/guide/wechat.png';
import QAIcon from '@/assets/icons/qa.png';
import UserAvatarIcon from '@/assets/icons/user-avatar.svg?url';
import AlarmIcon from '@/assets/icons/alarm.svg?url';

/**
 * 用户菜单浮层（V0.1 团队协作，PRD 1.1）
 *
 * 自上而下：摘要区（手机号/当前身份/米粒余额）→ 操作按钮（切换账户/充值/团队管理/升级会员，
 * 显隐按 PRD 状态表）→ 通用项（更新公告/问题反馈二维码/个人中心/相关协议/退出登录，原样迁移）。
 * 「切换账户」在其右侧弹出二级面板（SwitchAccountPanel，20261026 方案调整）。
 */
interface AccountMenuPopupProps {
  maskedMobile: string;
  /** 米粒余额（useSpaceCredits，null=未加载显 '--'） */
  credits?: number | null;
  accountList: AccountListItem[];
  loading: boolean;
  showSwitchList: boolean;
  hiddenUserMenuItems: string[];
  onShowSwitchList: () => void;
  onSwitchAccount: (account: AccountListItem) => void;
  onClose: () => void;
  onNavigate: (url: string) => void;
  /** 团队管理项暂去掉（20261027），恢复时与 visibility.showTeamManage 一起解注释 */
  onTeamManage?: () => void;
}

const menuItemStyle: React.CSSProperties = {
  display: 'flex',
  width: '100%',
  height: '40px',
  padding: '0 12px',
  alignItems: 'center',
  gap: '12px',
  cursor: 'pointer',
};

const menuTextStyle: React.CSSProperties = {
  color: '#FFF',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '16px',
};

const menuIconStyle: React.CSSProperties = {
  width: '16px',
  height: '16px',
  marginLeft: '6px',
  marginRight: '6px',
  flexShrink: 0,
};

const AccountMenuPopup: React.FC<AccountMenuPopupProps> = ({
  maskedMobile,
  credits,
  accountList,
  loading,
  showSwitchList,
  hiddenUserMenuItems,
  onShowSwitchList,
  onSwitchAccount,
  onClose,
  onNavigate,
  // onTeamManage, // 团队管理项暂去掉
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentSpace = useSelector(selectCurrentSpace);
  // 充值按钮注释隐藏后 visibility 暂无消费者；恢复「充值/升级会员/团队管理」时解注释
  // const { visibility } = useCurrentIdentity();
  const { hiddenUserMenuItems: brandHidden = [] } = useBrand();
  const { membershipInfo } = useUserPackage();
  const [isFeedbackHovered, setIsFeedbackHovered] = React.useState(false);

  const isHidden = (key: 'feedback' | 'profile' | 'agreements') =>
    brandHidden.includes(key) || hiddenUserMenuItems.includes(key);

  /** 摘要区身份文案：个人账户 / 团队名+角色（PRD 状态表）。
   * 拆两段渲染（20261030 主公定稿）：团队名过长时只省略名称段，
   * 分隔符+「拥有者/成员」flexShrink:0 固定展示，不再被整行省略挤掉 */
  const identityMain = currentSpace ? currentSpace.teamName : t('space.identityPersonal');
  const identityRole = currentSpace
    ? ` · ${currentSpace.role === TeamRole.Owner ? t('space.identityOwner') : t('space.identityMember')}`
    : '';

  /** 金色会员卡数据：个人=激活套餐名+有效期；团队=「团队会员」无有效期 */
  const activePackage = currentSpace
    ? undefined
    : membershipInfo?.packages?.find(p => p.isActive && p.planName !== '免费用户');
  const planName = currentSpace ? t('space.teamPlanLabel') : activePackage?.planName ?? t('space.freePlan');
  const validityText =
    activePackage
      ? t('space.membershipValidUntil', {
          date: activePackage.endTime.split(' ')[0]?.replace(/\//g, '-') || activePackage.endTime,
        })
      : undefined;
  void planName;
  void validityText;
  void activePackage;

  /** 退出登录（原 Sidebar handleLogout 迁移） */
  const handleLogout = async () => {
    try {
      const response = await authService.logout();
      if (response.code === 0) {
        message.success(t('user.logoutSuccess'));
      }
    } catch (error) {
      console.error('退出登录接口调用失败:', error);
    } finally {
      spaceManager.reset();
      try {
        await clearAllUserData();
      } catch (clearError) {
        console.error('清理用户数据失败:', clearError);
      }
      onClose();
      navigate('/home', { replace: true });
      eventBus.emit('forceLogout');
    }
  };

  /** 切换账户二级面板（自浮层右侧弹出） */
  const renderSwitchPanel = () => (
    <SwitchAccountPanel
      accountList={accountList}
      loading={loading}
      maskedMobile={maskedMobile}
      personalPlanName={
        !currentSpace
          ? membershipInfo?.packages?.find(p => p.isActive)?.planName
          : undefined
      }
      onSwitch={onSwitchAccount}
    />
  );

  return (
    <div
      style={{
        position: 'absolute',
        bottom: '60px',
        right: '24px',
        width: 'calc(100% - 48px)',
        borderRadius: '10px',
        background: '#2B2B2B',
        zIndex: 1000,
        boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
        padding: '8px 0',
      }}
      onClick={e => e.stopPropagation()}
    >
      {/* 二级下拉：点击「切换账户」自浮层右侧弹出，顶部与「切换账户」按钮文字中心对齐
          （20261029 主公定稿）。36px = 浮层 padding 8 + 摘要区 padding 6 + 底板 padding 10 + 按钮半高 12 */}
      {showSwitchList && (
        <div
          style={{
            position: 'absolute',
            left: '100%',
            top: '36px',
            marginLeft: '8px',
            zIndex: 1100,
          }}
          onClick={e => e.stopPropagation()}
        >
          {renderSwitchPanel()}
        </div>
      )}
      <>
        {/* ── 摘要区：黑色圆角底板内含 手机号+「切换账户」+ 身份行 + 米粒余额行（20261029 主公定稿）；金色会员卡本期不做（已注释）── */}
        <div style={{ padding: '6px 6px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          {/* 深色圆角底板（20261029 主公定稿）：身份行（手机号+切换账户+身份文案）与米粒余额行同处一块黑色底背景 */}
          <div style={{ background: '#000', borderRadius: '8px', padding: '10px 12px' }}>
          {/* 第一行：手机号与「切换账户」按钮同行 y 轴对齐；身份信息独立第二行（20261027 定稿） */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
            <div style={{ color: '#FFF', fontSize: '14px', fontWeight: 600, lineHeight: '20px', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {maskedMobile || t('user.defaultName')}
            </div>
            {/* 切换账户按钮：自摘要行右侧入口（原操作区「切换账户」项移此），样式按需求稿：⇄ 图标 + 细描边轻量胶囊；字号收小保证手机号完整展示 */}
            <button
              onClick={onShowSwitchList}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 4px',
                background: 'rgba(255,255,255,0.05)',
                color: 'rgba(255,255,255,0.95)',
                fontSize: '11px',
                fontWeight: 500,
                lineHeight: '14px',
                border: '1px solid rgba(255,255,255,0.16)',
                borderRadius: '8px',
                cursor: 'pointer',
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.28)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.16)';
              }}
            >
              <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                <path
                  d="M7 1.5L9 3.5L7 5.5"
                  stroke="rgba(255,255,255,0.9)"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 3.5H1.5"
                  stroke="rgba(255,255,255,0.9)"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                />
                <path
                  d="M5 10.5L3 8.5L5 6.5"
                  stroke="rgba(255,255,255,0.9)"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3 8.5H10.5"
                  stroke="rgba(255,255,255,0.9)"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                />
              </svg>
              {t('space.switchAccount')}
            </button>
          </div>
          {/* 第二行：团队身份信息（团队名 · 角色）独占一行——名称段超长省略，角色段固定不缩 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              color: 'rgba(255,255,255,0.55)',
              fontSize: '12px',
              lineHeight: '16px',
              marginTop: '2px',
            }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {identityMain}
            </span>
            {identityRole && <span style={{ flexShrink: 0 }}>{identityRole}</span>}
          </div>

          {/* 金色会员卡：本期不做（20261029 主公定稿），代码保留下次启用 */}
          {/* {(
            <div
              style={{
                marginTop: '10px',
                padding: '10px 12px',
                borderRadius: '8px',
                background: 'linear-gradient(123deg, #FEAE45 19.66%, #FF6D49 67.36%)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ color: '#1A1010', fontSize: '14px', fontWeight: 700, lineHeight: '20px' }}>
                  {planName}
                </span>
                {visibility.showUpgrade && (
                  <button
                    onClick={() => onNavigate('/ricegrain/membership')}
                    style={{
                      padding: '3px 10px',
                      background: 'rgba(0,0,0,0.25)',
                      color: '#FFE6C8',
                      fontSize: '12px',
                      fontWeight: 600,
                      border: 'none',
                      borderRadius: '999px',
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'rgba(0,0,0,0.4)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'rgba(0,0,0,0.25)';
                    }}
                  >
                    {t('space.upgradeMember')}
                  </button>
                )}
              </div>
              {validityText && (
                <div style={{ color: 'rgba(26,16,16,0.75)', fontSize: '12px', lineHeight: '16px', marginTop: '4px' }}>
                  {validityText}
                </div>
              )}
            </div>
          )} */}

          {/* 米粒余额行（按需求稿两行布局）：左列「米粒余额」+数字/单位、右「充值」。
              与上方身份行同处一块深色圆角底板（外层 wrapper，20261029 定稿）；
              数字走 credits prop，null/undefined 显 '--'；充值钮显隐按 PRD 状态表（成员隐藏） */}
          <div
            style={{
              marginTop: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
              <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '12px', lineHeight: '16px' }}>
                {t('space.memberBalance')}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '6px', minWidth: 0 }}>
                <span
                  style={{
                    color: '#FFF',
                    fontSize: '14px', // 与上方手机号同字号（20261029 主公定稿，原 20px）
                    fontWeight: 700,
                    lineHeight: '20px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {credits != null ? credits.toLocaleString() : '--'}
                </span>
                <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: '12px', flexShrink: 0 }}>
                  {t('space.creditsUnit')}
                </span>
              </span>
            </div>
            {/* 充值按钮：本次需求隐藏（20261029 主公定稿），恢复时解除注释 */}
            {/* {visibility.showRecharge && (
              <button
                onClick={() => onNavigate('/ricegrain/purchase')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#FF6D49',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  flexShrink: 0,
                  padding: 0,
                }}
              >
                {t('space.recharge')}
              </button>
            )} */}
          </div>
          </div>
        </div>

        {/* ── 操作按钮区：「团队管理」暂去掉（20261027 主公示），代码保留下次启用；「切换账户」入口已前移至摘要行右侧按钮 ── */}
        {/* {visibility.showTeamManage && (
          <div style={{ padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <div style={menuItemStyle} onClick={onTeamManage}>
              <svg {...svgProps}>
                <circle cx="6" cy="6" r="2.2" stroke="white" strokeWidth="1.2" fill="none" />
                <circle cx="10.5" cy="6" r="2.2" stroke="white" strokeWidth="1.2" fill="none" />
                <path d="M2 13c0-2 1.8-3.2 4-3.2s4 1.2 4 3.2M8.8 13c0-2 1.8-3.2 4-3.2" stroke="white" strokeWidth="1.2" fill="none" strokeLinecap="round" />
              </svg>
              <span style={menuTextStyle}>{t('space.teamManage')}</span>
            </div>
          </div>
        )} */}

          {/* ── 通用功能项（原样迁移自 Sidebar，含品牌门控）── */}
          <div style={{ padding: '6px 0' }}>
            <div style={menuItemStyle} onClick={() => onNavigate('/release-notes')}>
              <img src={AlarmIcon} alt={t('sidebar.updateAnnouncement')} style={{ ...menuIconStyle, filter: 'brightness(0) invert(1)' }} />
              <span style={menuTextStyle}>{t('sidebar.updateAnnouncement')}</span>
            </div>
            {!isHidden('feedback') && (
              <div
                style={{ ...menuItemStyle, position: 'relative' }}
                onMouseEnter={() => setIsFeedbackHovered(true)}
                onMouseLeave={() => setIsFeedbackHovered(false)}
              >
                <img src={QAIcon} alt={t('sidebar.feedback')} style={menuIconStyle} />
                <span style={menuTextStyle}>{t('sidebar.feedback')}</span>

                {isFeedbackHovered && (
                  <div
                    style={{
                      position: 'absolute',
                      left: '100%',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      marginLeft: '24px',
                      zIndex: 200,
                    }}
                    onMouseEnter={() => setIsFeedbackHovered(true)}
                    onMouseLeave={() => setIsFeedbackHovered(false)}
                    onClick={e => e.stopPropagation()}
                  >
                    <div
                      style={{
                        width: '196px',
                        height: '238px',
                        background: '#1a1a1a',
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                        padding: '0',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                      }}
                    >
                      <div
                        style={{
                          padding: '18px 18px 14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          textAlign: 'center',
                        }}
                      >
                        <span style={{ color: '#FFF', fontSize: '16px', fontWeight: 500 }}>
                          {t('common.scanQRFeedback')}
                        </span>
                      </div>
                      <div style={{ margin: '0 18px', display: 'flex', justifyContent: 'center' }}>
                        <div
                          style={{
                            width: '160px',
                            height: '160px',
                            background: '#FFF',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                          }}
                        >
                          <img
                            src={WeChatQRCode}
                            alt={t('common.wechatQRCode')}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            {!isHidden('profile') && (
              <div style={menuItemStyle} onClick={() => onNavigate('/user')}>
                <img src={UserAvatarIcon} alt={t('sidebar.personalCenter')} style={menuIconStyle} />
                <span style={menuTextStyle}>{t('sidebar.personalCenter')}</span>
              </div>
            )}
            {!isHidden('agreements') && (
              <div style={menuItemStyle} onClick={() => onNavigate('/agreement/user-agreement')}>
                <svg viewBox="0 0 1024 1024" width="16" height="16" style={{ ...menuIconStyle, transform: 'scale(0.8)' }}>
                  <path
                    d="M853.333333 938.666667H170.666667a42.666667 42.666667 0 0 1-42.666667-42.666667V128a42.666667 42.666667 0 0 1 42.666667-42.666667h682.666666a42.666667 42.666667 0 0 1 42.666667 42.666667v768a42.666667 42.666667 0 0 1-42.666667 42.666667z m-42.666666-85.333334V170.666667H213.333333v682.666666h597.333334zM341.333333 298.666667h341.333334v85.333333H341.333333V298.666667z m0 170.666666h341.333334v85.333334H341.333333v-85.333334z m0 170.666667h213.333334v85.333333H341.333333v-85.333333z"
                    fill="#ffffff"
                  />
                </svg>
                <span style={menuTextStyle}>{t('sidebar.relatedAgreements')}</span>
              </div>
            )}
            <div style={menuItemStyle} onClick={handleLogout}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" style={menuIconStyle}>
                <path d="M7.99723 2H2V14H8" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M11 11L14 8L11 5" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M5.33334 7.99707H14" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span style={menuTextStyle}>{t('sidebar.logout')}</span>
            </div>
          </div>
        </>
    </div>
  );
};

// 团队管理图标专用，恢复该项时解除注释
// const svgProps = {
//   width: 16,
//   height: 16,
//   viewBox: '0 0 16 16',
//   style: { ...menuIconStyle, transform: 'scale(0.8)' } as React.CSSProperties,
// };

export default AccountMenuPopup;
